import './SubscriptionInfo.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Header, List, ListItem, Placeholder, ScrollView } from '$uikit';
import { IcCard56, IcChevron16, IcDiamond, IcTonkeeper56 } from '$assets';
import { useTranslator } from '$hooks';
import { Loader, SubscriptionIcon, UnitLink } from '$shared/components';
import { Api } from '$api';
import { format, classNames as cn, _confirm, statReachGoal, confirmSheet } from '$utils';
import { toastActions } from '$store/toast';
import { useRoute } from 'react-router5';
import { CurrencyEnum, SubscriptionType, UserModel } from '$store/models';
import { mainActions } from '$store/main';
import router from '$navigation/router';
import { subscriptionPayRoute } from '$utils/subscriptionPayRoute';
import { SUBSCRIPTION_INFO } from '$shared/constants/pages';
import { goBack } from '$navigation/helper';
import { subscriptionsActions } from '$store/subscriptions';

export const SubscriptionInfo: FC = () => {
  const dispatch = useDispatch();
  const { route } = useRoute();
  const subscriptionId = route.params.id;
  const t = useTranslator();
  // Назад к списку. Ответ API может прийти после ухода с экрана — тогда никуда не уводим.
  const goToList = useCallback(() => {
    const state = router.getState();
    if (state?.name === SUBSCRIPTION_INFO && state.params.id === subscriptionId) {
      goBack();
    }
  }, [subscriptionId]);
  const [info, setInfo] = useState<{
    price: number;
    currency: string;
    activeAt: number;
    isRecurrent: boolean;
    type: 'ton' | 'card' | 'trial' | 'tondiamonds' | 'coins';
    invoiceId: string;
    category: SubscriptionType;
    authorId: number;
    author: UserModel;
    period: number;
    isTrial: boolean;
    isSuspended?: boolean;
    nextChargeAt?: number;
  } | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [isFailed, setFailed] = useState(false);

  const handleLoad = useCallback(() => {
    setLoading(true);
    setFailed(false);
    Api.get(`/balance/subscription/info/${subscriptionId}`)
      .then((data: any) => {
        setInfo(data);
        setLoading(false);
        data.author && dispatch(mainActions.setUser(data.author));
      })
      .catch((err) => {
        if (err.code === 'not_found') {
          // Подписку закрыли (например, после новой покупки) — убираем из списка и возвращаем к нему.
          setLoading(false);
          dispatch(subscriptionsActions.removeSubscription(Number(subscriptionId)));
          goToList();
        } else {
          setFailed(true);
        }
      });
  }, [subscriptionId]);

  const handleCancel = async () => {
    if (
      await confirmSheet(
        info?.isTrial
          ? t('subscription_trial_info_cancel_confirmation')
          : t('subscription_info_cancel_confirmation'),
      )
    ) {
      dispatch(toastActions.setToastLoading());
      Api.get(`/balance/subscription/${subscriptionId}/cancel`)
        .then(() => {
          dispatch(toastActions.setToastSuccess());
          statReachGoal('cancel_subscription', {
            type: info?.category ?? 0,
            trial: info?.isTrial ?? false,
          });
          if (info?.isSuspended) {
            // Приостановленная подписка после отмены закрывается на бэке целиком.
            dispatch(subscriptionsActions.removeSubscription(Number(subscriptionId)));
            goToList();
            return;
          }
          setInfo({
            ...info!,
            isRecurrent: false,
          });
        })
        .catch((err) => {
          dispatch(toastActions.setToastFail(err.message));
        });
    }
  };

  const handlePayNow = useCallback(() => {
    if (!info) {
      return;
    }
    const { name, params } = subscriptionPayRoute(info.category, info.authorId);
    router.navigate(name, params);
  }, [info]);

  const handleClickSubscriptionMethod = useCallback(() => {
    if (info?.invoiceId) {
      window
        .open(`https://app.tonkeeper.com/subscribe/${info.invoiceId}`, '_blank')
        ?.focus();
    }
  }, [info]);

  useEffect(() => {
    handleLoad();
  }, []);

  const subscriptionName = useMemo(() => {
    if (!info) {
      return null;
    }

    if (info.category === SubscriptionType.Adult) {
      return t('subscriptions_type_adult');
    } else if (info.category === SubscriptionType.Media) {
      return t('subscriptions_type_media');
    } else if (info.category === SubscriptionType.Basic) {
      return 'Ton.Place Basic';
    } else {
      return info.author.firstName + ' ' + info.author.lastName;
    }
  }, [info]);

  function renderCancelButton() {
    if (!info || !info?.isRecurrent) {
      return null;
    }

    if (info.type === 'trial' || info.type === 'tondiamonds') {
      return null;
    }

    if (info.type === 'ton') {
      if (!info.invoiceId) {
        return null;
      }

      return (
        <Button
          mode="secondary"
          href={`https://app.tonkeeper.com/subscribe/${info.invoiceId}`}
          target="_blank"
        >
          {t('subscription_info_cancel')}
        </Button>
      );
    } else {
      return (
        <Button mode="secondary" onClick={handleCancel}>
          {t('subscription_info_cancel')}
        </Button>
      );
    }
  }

  function renderContent() {
    if (isLoading) {
      return <Loader isWrapped />;
    }

    if (isFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={handleLoad}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (!info) {
      return null;
    }

    const price = info.price
      ? `${info.price}${
          info.currency === 'EUR' ? '€' : ` ${info.currency.toUpperCase()}`
        }`
      : t('subscription_price_free');

    return (
      <ScrollView>
        <UnitLink
          unit={info.author?.id ? info.author : undefined}
          className="SubscriptionInfo__logo_wrap"
        >
          <SubscriptionIcon type={info.category} authorId={info.authorId} />
          <div className="SubscriptionInfo__title">{subscriptionName}</div>
        </UnitLink>
        <div className="SubscriptionInfo__cont">
          {info.isSuspended && (
            <div className="SubscriptionInfo__suspended">
              <div className="SubscriptionInfo__suspended__text">
                {t('subscription_info_suspended_caption', {
                  defaultValue:
                    "We couldn't charge your payment, so access is paused. We'll retry automatically. To get access back now, pay for the membership manually.",
                })}
              </div>
              <Button onClick={handlePayNow}>
                {t('subscription_info_pay_now', { defaultValue: 'Pay now' })}
              </Button>
            </div>
          )}
          {info.currency !== CurrencyEnum.PLACE && (
            <div className="SubscriptionInfo__item">
              <div
                className={cn('SubscriptionInfo__method', { active: info.invoiceId })}
                onClick={handleClickSubscriptionMethod}
              >
                <div className="SubscriptionInfo__method__icon">
                  {info.type === 'card' ? (
                    <IcCard56 />
                  ) : info.type === 'tondiamonds' ? (
                    <IcDiamond />
                  ) : (
                    <IcTonkeeper56 />
                  )}
                </div>
                {info.type === 'trial' ? (
                  <div className="SubscriptionInfo__method__cont">
                    <div className="SubscriptionInfo__method__title">
                      {t('subscription_method_trial')}
                    </div>
                    <div className="SubscriptionInfo__method__caption">
                      {t('subscription_method_trial_caption')}
                    </div>
                  </div>
                ) : info.type === 'tondiamonds' ? (
                  <div className="SubscriptionInfo__method__cont">
                    <div className="SubscriptionInfo__method__title">TON Diamonds</div>
                    <div className="SubscriptionInfo__method__caption">
                      {t('subscription_price_free')}
                    </div>
                  </div>
                ) : (
                  <div className="SubscriptionInfo__method__cont">
                    <div className="SubscriptionInfo__method__title">
                      {info.type === 'card'
                        ? t('subscription_method_bank_card')
                        : 'Tonkeeper'}
                    </div>
                    <div className="SubscriptionInfo__method__caption">
                      {info.price}
                      {info.currency === 'EUR'
                        ? '€'
                        : ` ${info.currency.toUpperCase()}`}{' '}
                      /{' '}
                      {t('subscriptions_price_period', {
                        count: info.period,
                      })}
                    </div>
                  </div>
                )}
                {!!info.invoiceId && (
                  <div className="SubscriptionInfo__method__chevron">
                    <IcChevron16 />
                  </div>
                )}
              </div>
            </div>
          )}
          <List>
            {info.type !== 'trial' && (
              <>
                <ListItem
                  indicator={
                    info.isSuspended
                      ? t('subscription_info_status_suspended', {
                          defaultValue: 'Paused',
                        })
                      : info.isRecurrent || info.type === 'tondiamonds'
                      ? t('subscription_info_status_active')
                      : t('subscription_info_status_canceled')
                  }
                >
                  {t('subscription_info_status')}
                </ListItem>
                <ListItem indicator={price}>{t('subscription_info_price')}</ListItem>
                {info.type !== 'tondiamonds' && (
                  <ListItem
                    indicator={t('subscriptions_price_period', {
                      count: info.period,
                    })}
                  >
                    {t('subscription_info_interval')}
                  </ListItem>
                )}
              </>
            )}
            {info.isSuspended && !!info.nextChargeAt && (
              <ListItem indicator={format(info.nextChargeAt * 1000, 'EEE, d MMM yyyy')}>
                {t('subscription_info_next_charge', {
                  defaultValue: 'Next payment attempt',
                })}
              </ListItem>
            )}
            {info.type !== 'tondiamonds' && !!info.activeAt && (
              <ListItem indicator={format(info.activeAt * 1000, 'EEE, d MMM yyyy')}>
                {t('subscription_info_expires')}
              </ListItem>
            )}
          </List>
          <div className="SubscriptionInfo__cancel_button">{renderCancelButton()}</div>
        </div>
      </ScrollView>
    );
  }

  return (
    <>
      <Header showBackButton>{t('subscription_info_title')}</Header>
      {renderContent()}
    </>
  );
};
